/**
 * Primary application navigation: brand block, item list, optional section
 * groups, footer account block.
 * Spec: assets/handoffs/design_system_export/specs/SidebarNav.json
 *
 * The skip-to-content link and mobile drawer/focus-trap behavior the spec's
 * accessibility section calls for are page-level concerns, not something
 * this component can own — wire those where SidebarNav is mounted.
 *
 * @param {ReactNode=} brand
 * @param {Array<{label?:string,items:Array<{key,label,icon,href?,badge?,onClick?}>}>} sections
 * @param {string=} activeKey
 * @param {boolean=false} collapsed - icon-only rail; items fall back to title/aria-label
 * @param {function=} onNavigate - (key, item) => void
 * @param {{name,email,avatarSrc?,initials?,onMenuClick?}=} account
 */
import React from "react";
import classNames from "classnames";
import Avatar from "../Avatar/Avatar";
import Badge from "../Badge/Badge";
import IconButton from "../IconButton/IconButton";
import Icon from "../icons/Icon";
import styles from "./SidebarNav.module.scss";

const NavItem = ({ item, active, collapsed, onNavigate }) => {
  const Component = item.href ? "a" : "button";

  const handleClick = (e) => {
    if (!item.href) e.preventDefault();
    item.onClick?.(e);
    onNavigate?.(item.key, item);
  };

  return (
    <Component
      href={item.href}
      type={item.href ? undefined : "button"}
      className={classNames(styles.item, { [styles["item--active"]]: active })}
      aria-current={active ? "page" : undefined}
      aria-label={collapsed ? item.label : undefined}
      title={collapsed ? item.label : undefined}
      onClick={handleClick}
    >
      {item.icon && (
        <span className={styles.itemIcon} aria-hidden="true">
          {item.icon}
        </span>
      )}
      {!collapsed && <span className={styles.itemLabel}>{item.label}</span>}
      {!collapsed && item.badge != null && (
        <Badge color="gray" size="sm" className={styles.itemBadge}>
          {item.badge}
        </Badge>
      )}
    </Component>
  );
};

const SidebarNav = ({
  brand,
  sections = [],
  activeKey,
  collapsed = false,
  onNavigate,
  account,
  className,
  ...rest
}) => {
  return (
    <nav
      aria-label="Main"
      className={classNames(styles.root, { [styles["root--collapsed"]]: collapsed }, className)}
      {...rest}
    >
      {brand && <div className={styles.brand}>{brand}</div>}
      <div className={styles.sections}>
        {sections.map((section, sIndex) => (
          <div key={section.label || sIndex} className={styles.section}>
            {section.label && !collapsed && (
              <div className={styles.sectionLabel}>{section.label}</div>
            )}
            {section.items.map((item) => (
              <NavItem
                key={item.key}
                item={item}
                active={item.key === activeKey}
                collapsed={collapsed}
                onNavigate={onNavigate}
              />
            ))}
          </div>
        ))}
      </div>
      {account && (
        <div className={styles.footer}>
          <Avatar size="sm" src={account.avatarSrc} initials={account.initials} />
          {!collapsed && (
            <div className={styles.accountText}>
              <span className={styles.accountName}>{account.name}</span>
              <span className={styles.accountEmail}>{account.email}</span>
            </div>
          )}
          {!collapsed && (
            <IconButton
              size="sm"
              icon={<Icon name="DotsVertical" />}
              label="Account menu"
              onClick={account.onMenuClick}
            />
          )}
        </div>
      )}
    </nav>
  );
};

export default SidebarNav;
